import qs from "qs";
import xml2js from "xml2js";
import { unstable_rethrow } from "next/navigation";
import transformResultKeys from "@/utils/crm/transformResultKeys";

const CRM_API_BASE = process.env.CRM_API_BASE;
const CRM_ACCESS_CODE = process.env.CRM_ACCESS_CODE;
const CRM_GROUP_CODE = parseInt(process.env.CRM_GROUP_CODE);

const parseString = (xmlData) =>
  new Promise((resolve) => {
    xml2js.parseString(xmlData, (err, result) => {
      if (err) return resolve([]);
      resolve(result);
    });
  });

const sourceMapping = {
  rent: "RentListings",
  buy: "SalesListings",
  property: "GetProperties",
  agent: "GetAgents",
  countries: "GetCountry",
  states: "GetStates",
  cities: "GetCities",
  districts: "GetDistricts",
  communities: "GetCommunities",
  subCommunities: "GetSubCommunity",
  categories: "GetUnitCategory",
};

const sourceToKeyPath = {
  GetProperties: ["ArrayOfGetPropertiesData", "GetPropertiesData"],
  GetAgents: ["ArrayOfGetAgentData", "GetAgentData"],
  GetCountry: ["ArrayOfGetCountryData", "GetCountryData"],
  GetStates: ["ArrayOfGetStateData", "GetStateData"],
  GetCities: ["ArrayOfGetCityData", "GetCityData"],
  GetDistricts: ["ArrayOfGetDistrictData", "GetDistrictData"],
  GetCommunities: ["ArrayOfGetCommunityData", "GetCommunityData"],
  GetSubCommunity: ["ArrayOfGetSubCommunityData", "GetSubCommunityData"],
  GetUnitCategory: ["ArrayOfGetUnitCategoryData", "GetUnitCategoryData"],
};

/**
 * @param {string} purpose - The purpose of the operation. Should be one of:
 *   - "rent": Get listings for renting.
 *   - "buy": Get listings for buying.
 *   - "property": Get properties information.
 *   - "agent": Get agent details.
 *   - "categories": Get category information.
 *   - "countries": Get country information.
 *   - "states": Get state information.
 *   - "cities": Get city information.
 *   - "districts": Get district information.
 *   - "communities": Get community information.
 *   - "subCommunities": Get sub_community information.
 * @param {Object} searchParams
 * @param {number} revalidate
 * @param {Record<string, string>} replace - The map containing key replacements
 * @returns {Promise<*[]>}
 */
export default async function getCRMData(
  { purpose, searchParams = {}, revalidate = 100 },
  replace = {},
) {
  let source = sourceMapping[purpose];

  if (!source) {
    console.warn("Invalid purpose: " + purpose);
    return [];
  }

  const params = {
    AccessCode: CRM_ACCESS_CODE,
    GroupCode: CRM_GROUP_CODE,
    StartPriceRange: searchParams.price_min || "",
    EndPriceRange: searchParams.price_max || "",
    Bedrooms: searchParams.bedrooms || "",
    PropertyType: searchParams.type || "",
    SpecialProjects: "",
    FloorAreaMin: searchParams.size_min || "",
    FloorAreaMax: searchParams.size_max || "",
    UnitCategory: "",
    UnitID: searchParams.unit_id || "",
    BedroomsMax: "",
    PropertyID: searchParams.property_id || "",
    ReadyNow: "",
    PageIndex: "",
    AgentID: searchParams.agentId || "",
    CountryName: searchParams.country_name || "",
    CountryID: searchParams.country_id || "",
    StateID: searchParams.state_id || "",
    CityID: searchParams.city_id || "",
    DistrictID: searchParams.district_id || "",
    CategoryID: searchParams.category_id || "",
    CommunityID: searchParams.community_id || "",
    SubCommunityID: searchParams.sub_community_id || "",
  };

  const queryString = qs.stringify(params);
  const url = `${CRM_API_BASE}${source}?${queryString}`;
  let xmlData = null;
  const data = [];

  try {
    const response = await fetch(url, { next: { revalidate } });
    xmlData = await response.text();
  } catch (error) {
    unstable_rethrow(error);
    console.log(
      "************************* getCRMData **************************",
    );
    console.log(url);
    console.log(error.message);
    return data;
  }

  const result = await parseString(xmlData);

  const [arrayKey, itemKey] = sourceToKeyPath[source] ?? [];
  const response = result?.[arrayKey]?.[itemKey];

  if (response)
    return transformResultKeys(response, new Map(Object.entries(replace)));

  // Get the UnitDTO objects
  const unitDTOs = result["ArrayOfUnitDTO"]?.["UnitDTO"];
  // Loop over each UnitDTO
  unitDTOs?.forEach((unitDTO, index) => {
    for (let key in unitDTO) {
      let value = unitDTO[key][0];
      // Remove <![CDATA[ and ]]> if value is a string
      if (typeof value === "string") {
        if (value.trim() === "") {
          value = null;
        } else {
          value = value.replace(/<!\[CDATA\[/g, "").replace(/\]\]>/g, "");
        }
      }
      unitDTO[key] = value;
    }
    // format images
    if (unitDTO.Images?.Image?.length > 0) {
      const images = [];
      for (let i = 0; i < unitDTO.Images.Image.length; i++) {
        images.push(unitDTO.Images.Image[i].ImageURL[0]);
      }
      unitDTO.Images = images;
    }
    // format features
    if (unitDTO.FittingFixtures?.FittingFixture?.length > 0) {
      const FittingFixtures = [];
      for (let i = 0; i < unitDTO.FittingFixtures.FittingFixture.length; i++) {
        FittingFixtures.push(unitDTO.FittingFixtures.FittingFixture[i].Name[0]);
      }
      unitDTO.FittingFixtures = FittingFixtures;
    }

    data.push({
      id: unitDTO.code,
      size: unitDTO.BuiltupArea,
      tags: [],
      tour: null,
      code: unitDTO.code,
      isForSale: !!unitDTO.SellPrice,
      type: unitDTO.Category,
      price: parseInt(unitDTO.SellPrice || unitDTO.Rent),
      title: unitDTO.MarketingTitle,
      images: unitDTO.Images,
      bedrooms: unitDTO.Bedrooms,
      location: `${unitDTO.CityName ? unitDTO.CityName + "," : ""} ${unitDTO.DistrictName ? unitDTO.DistrictName + "," : ""} ${unitDTO.Community ? unitDTO.Community + "," : ""}`,
      amenities: unitDTO.FittingFixtures,
      bathrooms: unitDTO.NoOfBathrooms,
      description: unitDTO.Remarks,
      phone: unitDTO.ContactNumber,
      mobile: unitDTO.ContactNumber,
      email: unitDTO.SalesmanEmail,
      agentId: unitDTO.AgentID,
      agentReraNo: unitDTO.AgentReraNo,
      permitNumber: unitDTO.PermitNumber,
      googleCoordinates: unitDTO.ProGooglecoordinates,
      lastUpdated: unitDTO.LastUpdated,
      primaryUnitView: unitDTO.PrimaryUnitView,
      secondaryUnitView: unitDTO.SecondaryUnitView,
      parking: unitDTO.Parking,
      brochure: unitDTO.PDFBrochureLink,
      qrCode: unitDTO?.Documents?.Document?.[0]?.URL?.[0],
      url: "/" + purpose + "/" + unitDTO.code,
    });
  });

  return data;
}
